import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, ChefHat, MapPin, Clock, Users, Euro, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Bookings() {
  const navigate = useNavigate();
  const { token, isChef } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/bookings`, {
          headers: {
            'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setBookings(data);
        }
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [token]);

  const getStatusBadge = (status, paymentStatus) => {
    if (status === 'cancelled') return <Badge variant="destructive">Annulée</Badge>;
    if (status === 'completed') return <Badge variant="outline">Terminée</Badge>;
    if (paymentStatus === 'paid') return <Badge className="bg-success">Payée</Badge>;
    if (status === 'confirmed') return <Badge>Confirmée</Badge>;
    return <Badge variant="secondary">En attente</Badge>;
  };

  const upcomingBookings = bookings.filter(b => 
    ['pending', 'confirmed', 'paid'].includes(b.status)
  );
  const pastBookings = bookings.filter(b => 
    ['completed', 'cancelled'].includes(b.status)
  );

  const BookingCard = ({ booking }) => (
    <Card 
      className="dashboard-card cursor-pointer hover:shadow-medium transition-shadow"
      onClick={() => navigate(`/bookings/${booking.booking_id}`)}
      data-testid={`booking-card-${booking.booking_id}`}
    >
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
              <ChefHat className="w-6 h-6 text-accent" />
            </div>
            <div>
              <p className="font-heading font-semibold">
                {isChef ? booking.client_name : booking.chef_name}
              </p>
              <p className="text-sm text-muted-foreground">
                {isChef ? 'Client' : 'Chef'}
              </p>
            </div>
          </div>
          {getStatusBadge(booking.status, booking.payment_status)}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span>{booking.date}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span>{booking.start_time} • {booking.duration_hours}h</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span>{booking.guests} convives</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Euro className="w-4 h-4 text-muted-foreground" />
            <span className="font-semibold">{booking.total_price}€</span>
          </div>
        </div>

        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="line-clamp-1">{booking.address}</span>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="bookings-page">
      <div className="container-app">
        <div className="mb-10">
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mb-2">
            Mes Réservations
          </h1>
          <p className="text-muted-foreground">
            {isChef ? 'Gérez vos missions' : 'Suivez vos expériences culinaires'}
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">
              À venir ({upcomingBookings.length})
            </TabsTrigger>
            <TabsTrigger value="past" data-testid="tab-past">
              Passées ({pastBookings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            {upcomingBookings.length === 0 ? (
              <div className="text-center py-16">
                <Calendar className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">
                  Aucune réservation à venir
                </h3>
                <p className="text-muted-foreground mb-6">
                  {isChef 
                    ? 'Les demandes de réservation apparaîtront ici'
                    : 'Trouvez un chef pour votre prochain événement'}
                </p>
                {!isChef && (
                  <Button
                    className="btn-primary"
                    onClick={() => navigate('/chefs')}
                    data-testid="find-chef-btn"
                  >
                    Trouver un chef
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {upcomingBookings.map((booking) => (
                  <BookingCard key={booking.booking_id} booking={booking} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past">
            {pastBookings.length === 0 ? (
              <div className="text-center py-16">
                <Clock className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">
                  Aucune réservation passée
                </h3>
                <p className="text-muted-foreground">
                  Vos expériences terminées apparaîtront ici
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {pastBookings.map((booking) => (
                  <BookingCard key={booking.booking_id} booking={booking} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
